import { useEffect, useState } from "react"
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Card,
  Button,
  Spinner,
  Center,
  Image,
  Stack,
} from "@chakra-ui/react"
import { FaRedo } from "react-icons/fa"
import { refreshApp } from "../hooks/useVersionCheck"

interface Feature {
  title: string
  description: string
  mediaType: "image" | "video"
  mediaUrl: string
}

interface WhatsNewContent {
  version: string
  features: Feature[]
}

const WHATSNEW_URL = "https://hydrologiq-prod-dashboard.s3.eu-west-2.amazonaws.com/sections/whatsnew/content.json"

export const WhatsNew = () => {
  const [content, setContent] = useState<WhatsNewContent | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const response = await fetch(WHATSNEW_URL, {
          cache: "no-cache",
          headers: {
            "Cache-Control": "no-cache",
          },
        })

        if (!response.ok) {
          throw new Error(`Failed to fetch content: ${response.status}`)
        }

        const data: WhatsNewContent = await response.json()
        setContent(data)
      } catch (err) {
        console.error("Failed to fetch What's New content:", err)
        setError(err instanceof Error ? err.message : "Unknown error")
      } finally {
        setIsLoading(false)
      }
    }

    fetchContent()
  }, [])

  const handleRefresh = () => {
    refreshApp()
  }

  if (isLoading) {
    return (
      <Center minH="400px">
        <VStack gap={4}>
          <Spinner size="xl" color="blue.500" />
          <Text>Loading What's New...</Text>
        </VStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Container maxW="container.lg">
        <Card.Root>
          <Card.Body>
            <VStack gap={4} align="center">
              <Heading size="lg" color="red.500">
                Error Loading Content
              </Heading>
              <Text>{error}</Text>
              <Button onClick={() => window.location.reload()} colorPalette="blue">
                Retry
              </Button>
            </VStack>
          </Card.Body>
        </Card.Root>
      </Container>
    )
  }

  if (!content || content.features.length === 0) {
    return (
      <Container maxW="container.lg">
        <Card.Root>
          <Card.Body>
            <VStack gap={4} align="center">
              <Heading size="lg">No New Features</Heading>
              <Text>There are no new features to display at this time.</Text>
            </VStack>
          </Card.Body>
        </Card.Root>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg">
      <VStack gap={8} align="stretch">
        {/* Version Badge */}
        <Box textAlign="center">
          <Heading size="xl" mb={2}>
            What's New
          </Heading>
          <Text fontSize="sm" color="fg.muted">
            Version {content.version}
          </Text>
        </Box>

        {/* Features */}
        <Stack gap={6}>
          {content.features.map((feature, index) => (
            <Card.Root key={index}>
              <Card.Body>
                <VStack gap={4} align="stretch">
                  <Heading size="md">{feature.title}</Heading>
                  <Text color="primary">{feature.description}</Text>

                  {/* Media */}
                  {feature.mediaType === "image" && (
                    <Box borderRadius="md" overflow="hidden" bg="bg.subtle">
                      <Image
                        src={feature.mediaUrl}
                        alt={feature.title}
                        width="100%"
                        height="auto"
                        objectFit="contain"
                        fallback={
                          <Center h="200px" bg="bg.muted">
                            <Text color="gray.500">Image not available</Text>
                          </Center>
                        }
                      />
                    </Box>
                  )}

                  {feature.mediaType === "video" && (
                    <Box borderRadius="md" overflow="hidden" bg="bg.subtle">
                      <video
                        src={feature.mediaUrl}
                        controls
                        style={{ width: "100%", height: "auto" }}
                        onError={(e) => {
                          console.error("Video failed to load:", feature.mediaUrl)
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                  )}
                </VStack>
              </Card.Body>
            </Card.Root>
          ))}
        </Stack>

        {/* Refresh Button */}
        <Box textAlign="center" py={4}>
          <Button onClick={handleRefresh} colorPalette="blue" size="lg">
            <FaRedo />
            <Text ml={2}>Refresh App</Text>
          </Button>
          <Text fontSize="sm" color="fg.muted" mt={2}>
            Click to update to the latest version
          </Text>
        </Box>
      </VStack>
    </Container>
  )
}

